import React, { ChangeEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Styles from './accountStyle.module.scss';
import { LoaderCircle, RotateCcw, UserX, UserCheck } from 'lucide-react';
import Project from '../../common/Utils/project';
import { useAuth } from 'oidc-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../../components/services/apiService';
import Pagination from '../Pagination/Pagination';
import { DataTable, ColumnConfig } from '../Table';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import ConfirmationModal from '../Modal/ConfirmationModal';

type User = {
  email: string;
  name: string;
  customer: string;
  activated: string;
  roles: string;
};

// Mirrors AuthService's ResetPasswordResponseDto — resend-activation/deactivate-user report
// "user not found"/"already activated"/"already deactivated" as IsSuccess: false in an HTTP 200,
// not as an HTTP error, so the caller must check this body rather than relying on axios's catch.
type ActionResult = { isSuccess: boolean; message: string };

// Actions gated to the same roles the backend's [Roles(...)] attribute requires on
// resend-activation/deactivate-user — matching, not deriving from, UserManagementController.
const USER_ACTION_ROLES = ['globaladmin', 'reseller', 'tenantadmin'];

interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

const ITEMS_PER_PAGE = 20;

const AccountTable: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<User[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [statusChangeTarget, setStatusChangeTarget] = useState<{ email: string; activate: boolean } | null>(null);
  const [changingStatus, setChangingStatus] = useState(false);

  const totalPages = paginationInfo?.totalPages ?? 1;
  const navigate = useNavigate();
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { canEdit, role } = usePermissions();
  const canCreateAccount = canEdit('accessControl');
  const canManageUsers = USER_ACTION_ROLES.includes(role);
  const { addToast } = useToast();

  const handleResendActivation = async (email: string) => {
    setResendingEmail(email);
    try {
      const response = await api.post(
        `${Project.authApi}authservice/api/auth/resend-activation?emailId=${encodeURIComponent(email)}&tenantId=${selectedTenantId}`,
      );
      const result: ActionResult | undefined = response?.data?.result;
      addToast(result?.message || 'Activation email sent.', result?.isSuccess ? 'success' : 'warning', 4000, 'Resend Activation');
    } catch (error) {
      console.error('Error resending activation email:', error);
    } finally {
      setResendingEmail(null);
    }
  };

  const confirmStatusChange = async () => {
    if (!statusChangeTarget) return;
    const { email, activate } = statusChangeTarget;
    setChangingStatus(true);
    try {
      const response = await api.post(
        `${Project.authApi}authservice/api/auth/deactivate-user?emailId=${encodeURIComponent(email)}&tenantId=${selectedTenantId}&activate=${activate}`,
      );
      const result: ActionResult | undefined = response?.data?.result;
      addToast(
        result?.message || (activate ? 'User activated.' : 'User deactivated.'),
        result?.isSuccess ? 'success' : 'warning',
        4000,
        activate ? 'Activate User' : 'Deactivate User',
      );
      if (result?.isSuccess) {
        getTenantAccounts(currentPage, search);
      }
    } catch (error) {
      console.error(`Error ${activate ? 'activating' : 'deactivating'} user:`, error);
    } finally {
      setChangingStatus(false);
      setStatusChangeTarget(null);
    }
  };

  const columns: ColumnConfig[] = [
    {
      key: 'email',
      header: 'Email',
      width: canManageUsers ? '20%' : '24%',
      render: (value) => <div style={{ height: 32, display: 'flex', alignItems: 'center', paddingLeft: 12 }}>{value}</div>,
    },
    {
      key: 'name',
      header: 'Name',
      width: '16%',
      render: (value) => value || '-',
    },
    {
      key: 'customer',
      header: 'Tenant',
      width: '16%',
      render: (value) => value || '-',
    },
    {
      key: 'activated',
      header: 'Activated',
      width: '14%',
      render: (value) => (
        <span
          style={{
            display: 'inline-block',
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: value === 'Active' ? '#22c55e' : '#ef4444',
            marginLeft: 16,
          }}
        />
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      width: canManageUsers ? '14%' : '24%',
      render: (value) => value || '-',
    },
    ...(canManageUsers
      ? [
          {
            key: 'actions',
            header: 'Actions',
            width: '20%',
            render: (_: unknown, item: User) => (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, rowGap: 4 }}>
                {/* Resend only makes sense for a user who has never activated (Invited/AccountLocked)
                    — for a Deactive user, EmailConfirmed is already true from their first activation,
                    so the backend's resend-activation would just report "already activated" and send
                    nothing. Hidden here to avoid a button that silently does nothing. */}
                {item.activated !== 'Active' && item.activated !== 'Deactive' && (
                  <button
                    onClick={() => handleResendActivation(item.email)}
                    disabled={resendingEmail === item.email}
                    title="Resend activation email"
                    className="bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <RotateCcw size={12} className={resendingEmail === item.email ? 'animate-spin' : ''} />
                    Resend
                  </button>
                )}
                {item.activated === 'Deactive' ? (
                  <button
                    onClick={() => setStatusChangeTarget({ email: item.email, activate: true })}
                    title="Activate user"
                    className="bg-green-900/60 hover:bg-green-800 text-white px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors whitespace-nowrap"
                  >
                    <UserCheck size={12} />
                    Activate
                  </button>
                ) : (
                  <button
                    onClick={() => setStatusChangeTarget({ email: item.email, activate: false })}
                    title="Deactivate user"
                    className="bg-red-900/60 hover:bg-red-800 text-white px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors whitespace-nowrap"
                  >
                    <UserX size={12} />
                    Deactivate
                  </button>
                )}
              </div>
            ),
          } as ColumnConfig,
        ]
      : []),
  ];

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handlePageChange = (page: number) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const getTenantAccounts = async (page = 1, searchQuery = '') => {
    setLoading(true);
    try {
      const tenantId = selectedTenantId;
      const queryParams = new URLSearchParams({
        pageNumber: page.toString(),
        pageSize: ITEMS_PER_PAGE.toString(),
      });

      if (searchQuery.trim()) {
        queryParams.append('search', searchQuery.trim());
      }

      const response = await api.get(`${Project.authApi}authservice/api/auth/${tenantId}/get-tenant-users?${queryParams.toString()}`);
      const { data } = response;
      setAccounts(data.data ?? []);
      setPaginationInfo(data.pagination ?? null);
    } catch (error) {
      console.error('Error fetching accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTenantAccounts(currentPage, search);
  }, [currentPage, search]);

  return (
    <div className="text-white p-6 rounded-md shadow-lg">
      {/* Search bar & Create button */}
      <div className="flex justify-between items-center mb-4">
        <input
          type="text"
          placeholder="       Search"
          className={Styles['searchInput']}
          value={searchInput}
          onChange={handleSearch}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              setSearch(searchInput);
              setCurrentPage(1);
            }
          }}
        />
        {canCreateAccount && (
          <button className={Styles['createBtn']} onClick={() => navigate('/Accounts/CreateAccount')}>
            Create Account
          </button>
        )}
      </div>

      {/* Loader */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoaderCircle className="animate-spin w-10 h-10 text-white" />
        </div>
      ) : (
        <>
          {/* Data table */}
          <div className="mx-auto bg-1d1d1d text-white rounded-md shadow-md">
            <div className="overflow-x-auto">
              <DataTable
                columns={columns}
                data={accounts}
                loading={false}
                alternateRowColors={true}
                hoverEffect={true}
                containerStyle={{ background: 'transparent', borderRadius: 0, boxShadow: 'none' }}
                headerStyle={{
                  backgroundColor: '#1d1d1d',
                  color: '#9CA3AF',
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '0.75rem 0',
                  borderBottom: '2px solid #2a2a2a',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  minHeight: 48,
                }}
                rowStyle={{
                  fontSize: 15,
                  padding: '0.75rem 0',
                  borderBottom: '1px solid #3a3a3a',
                  minHeight: 44,
                }}
              />
              {accounts.length === 0 && !loading && <div className="px-4 py-6 text-center">No results.</div>}
            </div>
          </div>

          {/* Pagination controls */}
          <Pagination currentPage={currentPage} totalPages={totalPages} loading={loading} onPageChange={handlePageChange} />
        </>
      )}

      <ConfirmationModal
        show={statusChangeTarget !== null}
        title={statusChangeTarget?.activate ? 'Activate User' : 'Deactivate User'}
        description={
          statusChangeTarget?.activate
            ? `This marks ${statusChangeTarget?.email} as active.`
            : `This marks ${statusChangeTarget?.email} as deactivated.`
        }
        confirmLabel={statusChangeTarget?.activate ? 'Activate' : 'Deactivate'}
        loading={changingStatus}
        onConfirm={confirmStatusChange}
        onClose={() => setStatusChangeTarget(null)}
      />
    </div>
  );
};

export default AccountTable;
